import { useState, useEffect, useCallback } from 'react';
import { API_TIMEOUT, buildApiUrl } from '../config';

const apiRequest = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  const config = {
    headers: { ...defaultHeaders, ...options.headers },
    ...options
  };
  
  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.mensaje || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      data,
      error: data.error,
      mensaje: data.mensaje
    };
    
  } catch (error) {
    throw error;
  }
};

export const useTarjetas = (options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    immediate = false
  } = options;

  const fetchTarjetas = useCallback(async () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(buildApiUrl('tarjetas'), {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const validatedData = result.data || result;
      setData(validatedData);
      return validatedData;
    } catch (err) {
      const errorMessage = err.name === 'AbortError' 
        ? 'Tiempo de espera agotado' 
        : 'Error de conexión con el servidor';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (immediate) {
      fetchTarjetas();
    }
  }, [immediate, fetchTarjetas]);

  return {
    data,
    loading,
    error,
    refetch: fetchTarjetas,
  };
};

export const getTarjetaByNumero = async (numeroTarjeta) => {
  return apiRequest(`tarjeta/numero/${numeroTarjeta}`, { method: 'GET' });
};

export const getTarjetaByTelefono = async (telefono) => {
  return apiRequest(`tarjeta/${telefono}`, { method: 'GET' });
};

export const createTarjeta = async (tarjetaData) => {
  const dataToSend = {
    numeroTarjeta: tarjetaData.NumeroTarjeta || tarjetaData.numeroTarjeta,
    anioVencimiento: tarjetaData.AniVencimiento || tarjetaData.anioVencimiento,
    mesVencimiento: tarjetaData.MesVencimiento || tarjetaData.mesVencimiento,
    tipoTarjeta: tarjetaData.TipoTarjeta || tarjetaData.tipoTarjeta
  };
  return apiRequest('tarjeta', {
    method: 'POST',
    body: JSON.stringify(dataToSend)
  });
};

export const updateTarjeta = async (numeroTarjeta, tarjetaData) => {
  const dataToSend = {
    fechaCreacion: tarjetaData.FechaCreacion || tarjetaData.fechaCreacion,
    anioVencimiento: tarjetaData.AniVencimiento || tarjetaData.anioVencimiento,
    mesVencimiento: tarjetaData.MesVencimiento || tarjetaData.mesVencimiento,
    tipoTarjeta: tarjetaData.TipoTarjeta || tarjetaData.tipoTarjeta,
    estatus: tarjetaData.Estatus || tarjetaData.estatus,
    idCliente: tarjetaData.IdCliente || tarjetaData.idCliente
  };
  return apiRequest(`tarjeta/${numeroTarjeta}`, {
    method: 'PUT',
    body: JSON.stringify(dataToSend)
  });
};

export const deleteTarjeta = async (numeroTarjeta) => {
  return apiRequest(`tarjeta/${numeroTarjeta}`, {
    method: 'DELETE'
  });
};

export const altaMasivaTarjetas = async (tarjetas) => {
  const dataToSend = { tarjetas };
  return apiRequest('tarjetas/altaMasiva', {
    method: 'POST',
    body: JSON.stringify(dataToSend)
  });
};

export default {
  useTarjetas,
  getTarjetaByNumero,
  getTarjetaByTelefono,
  createTarjeta,
  updateTarjeta,
  deleteTarjeta,
  altaMasivaTarjetas
};
